import { formatDate } from '@/helpers';
import { Grid, Typography } from '@mui/material';
import { memo } from 'react';

import {
  convertHouseLotPropertyUse,
  convertImplantationInsertion,
  convertImprovements,
  convertInspectionType,
  convertOccupancyStatus,
  convertSolarOrientation,
  convertSurfaceType,
  convertUsageRestrictionLevel,
  convertViewQuality,
} from '../../../api/characteristics/enumerations';
import {
  LotEvaluatingUnit,
  SectionStatus,
} from '../../../api/characteristics/types';
import { SectionStatusBadge } from '../components/StatusIndicators';
import { AnswerTag, AnswerTagsBox, StyledDivider } from '../styles';

interface LotEvaluatingUnitSectionProps {
  formData: LotEvaluatingUnit;
  sectionStatus?: SectionStatus;
}

export const LotEvaluatingUnitSection = memo(function LotEvaluatingUnitSection({
  formData,
  sectionStatus,
}: LotEvaluatingUnitSectionProps) {
  return (
    <Grid container spacing={2} marginBottom={3}>
      <Grid item xs={12}>
        <SectionStatusBadge status={sectionStatus} />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="evaluating-unit-vistoria"
        >
          Vistoria
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Vistoria
            </Typography>
            <Typography>
              {formData.inspection.inspection_type
                ? convertInspectionType(formData.inspection.inspection_type)
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Data da Vistoria
            </Typography>
            <Typography>
              {formData.inspection.inspection_date
                ? formatDate(formData.inspection.inspection_date)
                : '-'}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="evaluating-unit-caracteristicas-gerais"
        >
          Características Gerais
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Destinação
            </Typography>
            <Typography>
              {formData.general_characteristics.property_use
                ? convertHouseLotPropertyUse(
                    formData.general_characteristics.property_use
                  )
                : '-'}
            </Typography>
          </Grid>
          {formData.general_characteristics.property_use_other && (
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Destinação - Outros
              </Typography>
              <Typography>
                {formData.general_characteristics.property_use_other}
              </Typography>
            </Grid>
          )}
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Nível de Restrição de Uso
            </Typography>
            <Typography>
              {formData.general_characteristics.usage_restriction_level
                ? convertUsageRestrictionLevel(
                    formData.general_characteristics.usage_restriction_level
                  )
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Superfície
            </Typography>
            <Typography>
              {formData.general_characteristics.surface
                ? convertSurfaceType(formData.general_characteristics.surface)
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Ocupação
            </Typography>
            <Typography>
              {formData.general_characteristics.occupancy_status
                ? convertOccupancyStatus(
                    formData.general_characteristics.occupancy_status
                  )
                : '-'}
            </Typography>
          </Grid>
          {formData.general_characteristics.occupancy_status_other && (
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Ocupação - Outros
              </Typography>
              <Typography>
                {formData.general_characteristics.occupancy_status_other}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="evaluating-unit-posicao-insercao"
        >
          Posição e Inserção
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Orientação Solar
            </Typography>
            <Typography>
              {formData.position_and_insertion.solar_orientation
                ? convertSolarOrientation(
                    formData.position_and_insertion.solar_orientation
                  )
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Vista
            </Typography>
            <Typography>
              {formData.position_and_insertion.view_quality
                ? convertViewQuality(
                    formData.position_and_insertion.view_quality
                  )
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Implantação/Inserção
            </Typography>
            <Typography>
              {formData.position_and_insertion.implantation_insertion
                ? convertImplantationInsertion(
                    formData.position_and_insertion.implantation_insertion
                  )
                : '-'}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="evaluating-unit-benfeitorias"
        >
          Benfeitorias
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <AnswerTagsBox>
          {(formData.improvements.items ?? []).length > 0 ? (
            (formData.improvements.items ?? []).map((v) => (
              <AnswerTag key={v}>{convertImprovements(v)}</AnswerTag>
            ))
          ) : (
            <Typography>-</Typography>
          )}
          {formData.improvements.items_other && (
            <AnswerTag>{formData.improvements.items_other}</AnswerTag>
          )}
        </AnswerTagsBox>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="evaluating-unit-uso-solo"
        >
          Uso e ocupação do solo
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Zoneamento
            </Typography>
            <Typography>{formData.use_and_occupation.zoning ?? '-'}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Usos permitidos pelo zoneamento
            </Typography>
            <Typography>
              {formData.use_and_occupation.allowed_uses ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Taxa de ocupação máxima (%)
            </Typography>
            <Typography>
              {formData.use_and_occupation.max_occupancy_rate_percent ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Altura máxima permitida (m)
            </Typography>
            <Typography>
              {formData.use_and_occupation.max_height_meters ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Coeficiente de aproveitamento (%)
            </Typography>
            <Typography>
              {formData.use_and_occupation.utilization_coefficient_percent ??
                '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Recuo frontal (m)
            </Typography>
            <Typography>
              {formData.use_and_occupation.front_setback_meters ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Recuo lateral (m)
            </Typography>
            <Typography>
              {formData.use_and_occupation.side_setback_meters ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Recuo fundos (m)
            </Typography>
            <Typography>
              {formData.use_and_occupation.back_setback_meters ?? '-'}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
});
